import React, { useMemo } from 'react';
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedCell} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import { getSchoolStatus, SCHOOL_STATUS_ORDER } from '../../../utils/dashboardStatus';
import { READINESS_BAND_COLORS, RISK_BAND_COLORS, CONTEXT_BAND_COLORS, DEPLOYMENT_ACTION_BAND_COLORS } from '../constants';

import {UNIT, fmtValue} from '../../DashboardCommon/units';
import {BandChip} from '../../DashboardCommon/widgets.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';
const NAVY   = '#052F44';

/* ── Status chip config (matching AttentionNeeded.page.jsx) ────────── */
// getSchoolStatus returns "Growth Opportunity" but the colour key is "Growth",
// which left that chip grey.
const getStatusColor = (label) => {
    return DEPLOYMENT_ACTION_BAND_COLORS[label] || (label === 'Growth Opportunity' ? DEPLOYMENT_ACTION_BAND_COLORS.Growth : undefined);
};

const StatusChip = ({ label }) => <BandChip label={label} color={getStatusColor(label) || '#6B7280'}/>;

/* ── Shared table cell styles ───────────────────────────────────── */
const headCell = {
    backgroundColor: NAVY,
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 700,
    borderBottom: 'none',
    py: 2,
    px: 1.5,
    lineHeight: 1.3,
};

const bodyCell = {
    fontSize: '14px',
    color: '#374151',
    py: 2,
    px: 1.5,
    borderBottom: '1px solid #F3F4F6',
    textAlign: 'center',
};

/* ── Sort / filter meta (QA 9 Oct 2026: every column sorts and filters from its header) ──
   The rows are built below in this page's own shape, so each column reads its value through an
   accessor: the "active (registered)" cells sort by the active count (kept as a number on the
   row), the score cells by their shown figure — null when the cell is under the n floor. */
const scoreOf = (c) => (!c || c.suppressed ? null : c.val);
const STATUS_ORDER = ['Growth', 'Growth Opportunity', 'Active', 'High risk', 'Low coverage', 'Data weak', 'Dormant'];
const COLUMNS = [
    {key: 'country', label: 'Country', type: 'text'},
    {key: 'activeSchools', label: 'Active Schools (Registered)', type: 'number', accessor: (r) => r.activeSchoolsNum},
    {key: 'activeParticipants', label: 'Active Participants (Registered)', type: 'number', accessor: (r) => r.activeParticipantsNum},
    {key: 'participation', label: 'Participation Rate', type: 'percent', accessor: (r) => r.participation?.val},
    {key: 'readiness', label: 'Readiness Composite (score)', type: 'number', accessor: (r) => scoreOf(r.readiness)},
    {key: 'anyRisk', label: 'Any Cyber Risk (%)', type: 'percent', accessor: (r) => scoreOf(r.anyRisk)},
    {key: 'context', label: 'Context & Support Composite (score)', type: 'number', accessor: (r) => scoreOf(r.context)},
    {key: 'status', label: 'Status', type: 'band', order: STATUS_ORDER},
];
const asNumber = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
};

const DEPLOYMENT_MAP_SECTION_CODE = 'S_6';
const CARD_SECTION = DEPLOYMENT_MAP_SECTION_CODE;

const DeploymentMapTab = () => {
   const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });

    // No Renewal Date column — removed per bug sheet SM-PD-30Sep-1 (struck
    // through on the Portfolio Baseline table).

    const sectionData = dashboardData?.cards?.sections?.find(
        (section) => section.section_code === DEPLOYMENT_MAP_SECTION_CODE
    );

    const titleCard = sectionData?.cards?.find(card => card.card_type === 'TitleCard');

    // Mockup title (PD slide 47) when the card sends none.
    const pageTitle = titleCard?.title || 'Portfolio Baseline';
    // Bug sheet Ian-PD (Title is missing, 24 Sep): the page subtitle.
    const pageSubtitle = titleCard?.subtitle || titleCard?.footer || 'Where is deployment active, dormant, under-covered, or ready for expansion?';

    const rows = useMemo(() => {
        const tableCard = sectionData?.cards?.find(card => card.card_type === 'TableCard');
        const columns = tableCard?.table_objects || [];
        if (columns.length === 0) {
            return [];
        }

        const findCol = (identifiers) => {
            return columns.find(col => 
                identifiers.includes(col.column_name) || 
                identifiers.includes(col.column_code)
            );
        };

        const countryCol = findCol(['Country', 'Country / Market', 'country']);
        const activeSchoolsCol = findCol(['Active Schools']);
        const registeredSchoolsCol = findCol(['Registered Schools', 'Schools', 'schools']);
        const activeParticipantsCol = findCol(['Active Participants']);
        const registeredParticipantsCol = findCol(['Registered Participants', 'Students', 'students']);
        const participationCol = findCol(['Participation %', 'Participation Rate', 'Participation', 'participation']);
        const readinessCol = findCol(['COS_ReadinessAVG', 'Readiness Composite']);
        const anyRiskCol = findCol(['expe_CPCI_CyberRisks', 'Any-risk', 'Any Cyber risk']);
        const contextCol = findCol(['COS_ContextAVG', 'Context Composite', 'Context & Support Composite']);

        const dormantCol = findCol(['Dormant Status', 'Dormant']);
        const atRiskCol = findCol(['At Risk Status', 'At Risk', 'High Risk Status', 'High Risk']);
        const lowParticipationCol = findCol(['Low Participation Status', 'Low Participation', 'Low Coverage Status', 'Low Coverage']);
        const dataWeakCol = findCol(['Data Weak Status', 'Data Weak']);
        const activeStatusCol = findCol(['Active Status', 'Active']);
        const growthCol = findCol(['Growth Opportunity Status', 'Growth Opportunity']);

        const refCol = countryCol || columns[0];
        const numRows = refCol?.values?.length || 0;
        const dynamicRows = [];

        const formatNum = (val) => {
            if (val === null || val === undefined) return '0';
            const num = parseFloat(val);
            if (isNaN(num)) return val;
            if (num >= 1000000) return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
            if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}k`;
            return num.toString();
        };

        const formatParticipantNum = (val) => {
            if (val === null || val === undefined) return '0';
            const num = parseFloat(val);
            if (isNaN(num)) return val;
            if (num >= 1000000) return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
            if (num >= 10000) return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}k`;
            return num.toLocaleString();
        };

        const formatParticipation = (val) => {
            if (val === null || val === undefined || val === '') return '0%';
            if (typeof val === 'number') return `${val}%`;
            if (typeof val === 'string' && !val.endsWith('%')) return `${val}%`;
            return val;
        };

        for (let i = 0; i < numRows; i++) {
            const country = countryCol?.values?.[i] || '';

            const activeSchools = activeSchoolsCol?.values?.[i] ?? registeredSchoolsCol?.values?.[i] ?? 0;
            const registeredSchools = registeredSchoolsCol?.values?.[i] ?? activeSchoolsCol?.values?.[i] ?? 0;
            const activeSchoolsStr = formatNum(activeSchools);
            const registeredSchoolsStr = `(${formatNum(registeredSchools)})`;

            const activeParticipants = activeParticipantsCol?.values?.[i] ?? registeredParticipantsCol?.values?.[i] ?? 0;
            const registeredParticipants = registeredParticipantsCol?.values?.[i] ?? activeParticipantsCol?.values?.[i] ?? 0;
            const activeParticipantsStr = formatParticipantNum(activeParticipants);
            const registeredParticipantsStr = `(${formatParticipantNum(registeredParticipants)})`;

            const participationVal = formatParticipation(participationCol?.values?.[i]);
            const participationBand = participationCol?.cell_data?.[i]?.band || null;

            // A score cell under the n floor (cell_data.suppressed) shows the placeholder and no band;
            // the row itself stays — its schools, participants and status are real.
            const scoreCell = (col, unit) => {
                const cd = col?.cell_data?.[i];
                const suppressed = isSuppressedCell(cd);
                return {val: suppressed ? null : fmtValue(col?.values?.[i], unit), band: suppressed ? null : (cd?.band || null), suppressed};
            };
            const readiness = scoreCell(readinessCol);
            const anyRisk = scoreCell(anyRiskCol, UNIT.PERCENT);
            const context = scoreCell(contextCol);

            const statusData = {
                dormant: dormantCol?.values?.[i],
                highRisk: atRiskCol?.values?.[i],
                lowCoverage: lowParticipationCol?.values?.[i],
                dataWeak: dataWeakCol?.values?.[i],
                active: activeStatusCol?.values?.[i],
                growth: growthCol?.values?.[i],
            };

            const status = getSchoolStatus(statusData);

            dynamicRows.push({
                country,
                activeSchools: activeSchoolsStr,
                activeSchoolsNum: asNumber(activeSchools),
                registeredSchools: registeredSchoolsStr,
                activeParticipants: activeParticipantsStr,
                activeParticipantsNum: asNumber(activeParticipants),
                registeredParticipants: registeredParticipantsStr,
                participation: { val: participationVal, band: participationBand },
                readiness,
                anyRisk,
                context,
                status,
            });
        }

        return dynamicRows;
    }, [sectionData]);

    // Below the row memo and above the loading branches: hooks run on every render.
    const t = useTableControls(rows, COLUMNS);

    if (loading) {
        return <CircularLoader />;
    }

    if (!sectionData) {
        return (
            <NoDataAvailable />
        );
    }

    return (
        <Box sx={{ mt: 1.5 }}>
            {/* Title */}
            <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, mb: 0.5}}>
                {pageTitle}
            </Typography>

            {/* Subtitle */}
            <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#4B5563', mb: 3}}>
                {pageSubtitle}
            </Typography>

            {/* Status legend — every band, not only those present in the table (PD slide 43). */}
            {rows.length > 0 && (
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2.5}}>
                    {SCHOOL_STATUS_ORDER.map((label) => (
                        <Box key={label} sx={{
                            display: 'inline-block',
                            backgroundColor: getStatusColor(label) || '#6B7280', color: '#ffffff',
                            fontSize: '13px', fontWeight: 600, px: 1.75, py: 0.6, borderRadius: '20px',
                        }}>
                            {label}
                        </Box>
                    ))}
                </Box>
            )}

            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('country')} sx={{...headCell, textAlign: 'left', width: '15%', pl: 2.5}}/>
                            <SortFilterHeaderCell {...t.headerProps('activeSchools')} sx={{...headCell, textAlign: 'center', width: '13%'}}>
                                Active Schools<br />(Registered)
                            </SortFilterHeaderCell>
                            <SortFilterHeaderCell {...t.headerProps('activeParticipants')} sx={{...headCell, textAlign: 'center', width: '14%'}}>
                                Active Participants<br />(Registered)
                            </SortFilterHeaderCell>
                            <SortFilterHeaderCell {...t.headerProps('participation')} sx={{...headCell, textAlign: 'center', width: '11%'}}>
                                Participation<br />Rate
                            </SortFilterHeaderCell>
                            <SortFilterHeaderCell {...t.headerProps('readiness')} sx={{...headCell, textAlign: 'center', width: '12%'}}>
                                Readiness<br />Composite (score)
                            </SortFilterHeaderCell>
                            <SortFilterHeaderCell {...t.headerProps('anyRisk')} sx={{...headCell, textAlign: 'center', width: '11%'}}>
                                Any<br />Cyber Risk (%)
                            </SortFilterHeaderCell>
                            <SortFilterHeaderCell {...t.headerProps('context')} sx={{...headCell, textAlign: 'center', width: '13%'}}>
                                Context & Support<br />Composite (score)
                            </SortFilterHeaderCell>
                            <SortFilterHeaderCell {...t.headerProps('status')} sx={{...headCell, textAlign: 'center', width: '11%'}}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} sx={{...bodyCell, py: 4, color: '#6A7282'}}>No country matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, i) => (
                            <TableRow key={row.country + '-' + rows.indexOf(row)} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>
                                    {row.country}
                                </TableCell>
                                <TableCell sx={bodyCell}>
                                    <Box>{row.activeSchools}</Box>
                                    <Box sx={{fontSize: '12px', color: '#6B7280', lineHeight: 1.2}}>{row.registeredSchools}</Box>
                                </TableCell>
                                <TableCell sx={bodyCell}>
                                    <Box>{row.activeParticipants}</Box>
                                    <Box sx={{fontSize: '12px', color: '#6B7280', lineHeight: 1.2}}>{row.registeredParticipants}</Box>
                                </TableCell>
                                <TableCell sx={bodyCell}>
                                    <Box>{row.participation.val}</Box>
                                </TableCell>
                                <TableCell sx={bodyCell}>
                                    <Box>{row.readiness.suppressed ? <SuppressedValue variant="cell"/> : row.readiness.val}</Box>
                                    {row.readiness.band && (
                                        <Box sx={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: READINESS_BAND_COLORS[row.readiness.band],
                                            lineHeight: 1.2,
                                        }}>
                                            {row.readiness.band}
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell sx={bodyCell}>
                                    <Box>{row.anyRisk.suppressed ? <SuppressedValue variant="cell"/> : row.anyRisk.val}</Box>
                                    {row.anyRisk.band && (
                                        <Box sx={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: RISK_BAND_COLORS[row.anyRisk.band],
                                            lineHeight: 1.2,
                                        }}>
                                            {row.anyRisk.band}
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell sx={bodyCell}>
                                    <Box>{row.context.suppressed ? <SuppressedValue variant="cell"/> : row.context.val}</Box>
                                    {row.context.band && (
                                        <Box sx={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: CONTEXT_BAND_COLORS[row.context.band],
                                            lineHeight: 1.2,
                                        }}>
                                            {row.context.band}
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell sx={{...bodyCell, px: 1.5}}>
                                    <StatusChip label={row.status}/>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default DeploymentMapTab;

